import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, ExternalLink, X } from 'lucide-react';
import { MCP_CONNECTIONS, McpAgentId } from '../config/mcpConnections';
import './McpConnectionDialog.css';

interface McpConnectionDialogProps {
  onClose: () => void;
}

type CopyState = 'idle' | 'pending' | 'copied' | 'error';

const McpConnectionDialog: React.FC<McpConnectionDialogProps> = ({ onClose }) => {
  const id = useId();
  const dialog = useRef<HTMLElement>(null);
  const tabs = useRef<Partial<Record<McpAgentId, HTMLButtonElement | null>>>({});
  const copyAttempt = useRef(0);
  const [activeAgent, setActiveAgent] = useState<McpAgentId>('codex');
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const connection = MCP_CONNECTIONS.find(agent => agent.id === activeAgent)!;

  useEffect(() => {
    const opener = document.activeElement;
    tabs.current.codex?.focus();
    return () => {
      copyAttempt.current += 1;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  const selectAgent = (agent: McpAgentId) => {
    copyAttempt.current += 1;
    setCopyState('idle');
    setActiveAgent(agent);
  };

  const copySnippet = async () => {
    const attempt = ++copyAttempt.current;
    setCopyState('pending');
    try {
      await navigator.clipboard.writeText(connection.snippet);
      if (attempt === copyAttempt.current) setCopyState('copied');
    } catch {
      if (attempt === copyAttempt.current) setCopyState('error');
    }
  };

  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.defaultPrevented) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], [tabindex="0"]',
    ) || []).filter(element => element.tabIndex >= 0 && !element.closest('[hidden]'));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % MCP_CONNECTIONS.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + MCP_CONNECTIONS.length) % MCP_CONNECTIONS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = MCP_CONNECTIONS.length - 1;
    else return;
    event.preventDefault();
    const nextAgent = MCP_CONNECTIONS[nextIndex].id;
    selectAgent(nextAgent);
    tabs.current[nextAgent]?.focus();
  };

  return createPortal(
    <div className="mcp-connect-backdrop" onClick={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section
        className="mcp-connect-dialog"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        onKeyDown={handleDialogKeyDown}
      >
        <header className="mcp-connect-header">
          <h2 id={`${id}-title`}>Connect your agent</h2>
          <button type="button" className="mcp-connect-close" aria-label="Close agent connection" onClick={onClose}>
            <X size={20} aria-hidden="true" />
          </button>
        </header>
        <div className="mcp-connect-tabs" role="tablist" aria-label="AI agent">
          {MCP_CONNECTIONS.map((agent, index) => (
            <button
              type="button"
              role="tab"
              key={agent.id}
              id={`${id}-tab-${agent.id}`}
              aria-controls={`${id}-panel`}
              aria-selected={activeAgent === agent.id}
              tabIndex={activeAgent === agent.id ? 0 : -1}
              ref={element => { tabs.current[agent.id] = element; }}
              onClick={() => selectAgent(agent.id)}
              onKeyDown={event => handleTabKeyDown(event, index)}
            >
              {agent.label}
            </button>
          ))}
        </div>
        <div className="mcp-connect-panel" id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-tab-${activeAgent}`}>
          <div className="mcp-connect-snippet">
            <div className="mcp-connect-snippet-header">
              <span id={`${id}-snippet-label`}>{connection.snippetLabel}</span>
              <button
                type="button"
                className="mcp-connect-copy"
                aria-label={`Copy ${connection.label} connection`}
                disabled={copyState === 'pending'}
                onClick={copySnippet}
              >
                {copyState === 'copied' ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
                {copyState === 'copied' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre tabIndex={0} aria-labelledby={`${id}-snippet-label`}><code>{connection.snippet}</code></pre>
          </div>
          <p className="mcp-connect-instruction">{connection.instruction}</p>
          {(connection.action || connection.guideUrl) && <div className="mcp-connect-footer">
            {connection.action && <a className="mcp-connect-install" href={connection.action.href}>{connection.action.label}</a>}
            {connection.guideUrl && <a className="mcp-connect-guide" href={connection.guideUrl} target="_blank" rel="noopener noreferrer">
              Setup guide <ExternalLink size={14} aria-hidden="true" />
            </a>}
          </div>}
          <p className={`mcp-connect-copy-status${copyState === 'error' ? ' is-error' : ''}`} role="status" aria-live="polite">
            {copyState === 'copied' && 'Copied to clipboard.'}
            {copyState === 'error' && 'Couldn’t copy. Select and copy the text above.'}
          </p>
        </div>
      </section>
    </div>,
    document.body,
  );
};

export default McpConnectionDialog;
